// Messages for the Amai Mochi screens. Spanish (es-MX) is the business language;
// English mirrors it so the keys never render raw in the en/fr catalogs.
// Spanish for react-admin and the CRM screens lives in spanishRaMessages.ts and
// spanishCrmMessages.ts.

export const englishAmaiMessages = {
  amai: {
    navigation: {
      label: "Amai Mochi navigation",
      new_order: "New order",
      orders: "Orders",
      receivables: "To collect",
      summary: "Summary",
      settings: "Settings",
    },
    catalog: {
      title: "Catalog",
    },
    flavors: {
      add: "New flavor",
      create_title: "New flavor",
      edit_title: "Edit flavor",
      empty: "No flavors yet. Add the first one.",
      inactive: "Inactive",
      active_toggle: "Active: %{name}",
      duplicate: "A flavor with that name already exists.",
      color_hint: "Shown next to the flavor in orders and charts.",
      invalid_color: "Pick a color, like #EE8FA8.",
    },
    orders: {
      delivery: "Delivery",
      delivery_options: {
        manana: "Tomorrow",
        pasado: "Day after tomorrow",
        otra: "Other date",
      },
      delivery_date: "Delivery date",
      pieces: "%{smart_count} piece |||| %{smart_count} pieces",
      pieces_of: "Pieces of %{name}",
      remove_one: "Remove one piece of %{name}",
      add_five: "Add 5 pieces of %{name}",
      no_flavors: "No active flavors. Add them in Settings → Flavors.",
      minimum_ok: "Meets the %{minimum}-piece minimum",
      minimum_missing:
        "%{missing} pieces short of the %{minimum}-piece minimum",
      payment: "Payment on delivery",
      payment_methods: {
        efectivo: "Cash",
        transferencia: "Transfer",
      },
      location_and_notes: "Notes and location",
      location: "Location",
      location_placeholder: "Address or Maps link",
      notes: "Notes",
      save: "Save order",
      saving: "Saving…",
      saved: "Order saved. It is pending delivery and payment.",
      save_error:
        "The order could not be saved. Check your connection and try again.",
      missing_client: "Pick a client",
      missing_pieces: "Add at least one flavor",
      missing_date: "Pick the delivery date",
      view_orders: "View orders",
      repeat: {
        last: "Last order (%{date}):",
        button: "Repeat last order",
      },
      confirm: {
        button: "Confirm on WhatsApp",
        greeting: "Hi %{name}, this is Amai Mochi confirming your order:",
        total:
          "Total: %{smart_count} piece · %{total} |||| Total: %{smart_count} pieces · %{total}",
        total_no_price:
          "Total: %{smart_count} piece |||| Total: %{smart_count} pieces",
        delivery: "Delivery: %{date}",
        payment: "Payment on delivery: %{method}",
        thanks: "Thank you!",
      },
      filter: "Show",
      filters: {
        pendiente: "Pending",
        entregado: "Delivered",
        cancelado: "Cancelled",
        todos: "All",
      },
      states: {
        pendiente: "Pending",
        entregado: "Delivered",
        cancelado: "Cancelled",
      },
      days: {
        yesterday: "Yesterday",
        today: "Today",
        tomorrow: "Tomorrow",
      },
      empty: "No orders here.",
      no_price: "No price",
      actions: {
        deliver: "Delivered",
        edit: "Edit",
        cancel: "Cancel",
        back_to_pending: "Back to pending",
      },
      notice: {
        delivered: "Order marked as delivered",
        cancelled: "Order cancelled",
        pending: "The order is pending again",
      },
      edit_title: "Edit order",
      save_changes: "Save changes",
      updated: "Order updated",
      only_pending: "Only pending orders can be edited.",
      open_maps: "Open %{address} in Maps",
    },
    production: {
      title: "Production",
      for: "Prepare for",
      days: {
        hoy: "Today",
        manana: "Tomorrow",
        pasado: "Day after tomorrow",
      },
      total: "%{smart_count} piece |||| %{smart_count} pieces",
      orders: "for %{smart_count} order |||| for %{smart_count} orders",
      per_flavor: "Pieces per flavor",
      empty: "No pending orders for that day.",
    },
    deliveries: {
      title: "Today's deliveries",
      progress: "%{done} of %{total} delivered · %{pieces} pieces to go",
      overdue: "Overdue",
      empty: "No deliveries for today.",
      all_orders: "See all orders",
    },
    summary: {
      title: "Your week",
      previous_week: "Previous week",
      next_week: "Next week",
      sold: "You sold",
      vs_last_week: "%{change} vs. last week",
      orders: "Orders",
      average_pieces:
        "%{smart_count} pc on average |||| %{smart_count} pcs on average",
      collected: "Collected",
      to_collect: "To collect",
      expenses: "Expenses",
      profit: "Profit",
      profit_hint: "Sales minus expenses",
      no_sales: "No deliveries this week.",
      pieces_per_day: "Pieces per day",
      day_pieces:
        "%{day}: %{smart_count} piece |||| %{day}: %{smart_count} pieces",
      top_flavors: "Best-selling flavors",
      flavor_pieces:
        "%{name}: %{smart_count} piece |||| %{name}: %{smart_count} pieces",
      by_client: "By client",
    },
    receivables: {
      summary: "Summary of what is owed",
      owed_total: "Owed to you in total",
      all_paid: "All collected",
      counts:
        "%{smart_count} order · %{overdue} older than %{days} days |||| %{smart_count} orders · %{overdue} older than %{days} days",
      filters: {
        todos: "All",
        vencidos: "Older than %{days} days",
      },
      none_overdue: "No client has orders older than %{days} days.",
      order_line: "%{date} · %{pieces} pcs",
      delivered_today: "today",
      days_ago: "%{smart_count} day ago |||| %{smart_count} days ago",
      remind: "Remind",
      reminded: "Reminded",
      no_phone: "No phone to remind",
      register_payment: "Register payment",
      how_paid: "How did they pay?",
      paid: "Payment from %{name} registered",
      pay_error:
        "The payment could not be registered. Check your connection and try again.",
      undone: "Payment undone",
      collected_today: "Collected today",
      no_price_hint: "An order has no price yet: set prices to collect it.",
      reminder:
        "Hi %{name}, this is Amai Mochi. A friendly reminder of the pending payment of %{total} for %{smart_count} order. Thank you! |||| Hi %{name}, this is Amai Mochi. A friendly reminder of the pending payment of %{total} for %{smart_count} orders. Thank you!",
    },
    export: {
      title: "Export to Excel",
      intro:
        "Download a month's sales, payments or expenses in a file that opens in Excel, to send to your accountant.",
      month: "Month",
      reports: {
        ventas: "Sales (delivered orders)",
        cobros: "Payments received",
        gastos: "Expenses",
      },
      empty: "Nothing to export in that month.",
      columns: {
        date: "Date",
        order: "Order",
        client: "Client",
        flavors: "Flavors",
        pieces: "Pieces",
        payment: "Payment method",
        total: "Total",
        reference: "Reference",
        amount: "Amount",
        category: "Category",
        note: "Note",
      },
    },
    money: {
      title: "Money",
    },
    legal: {
      privacy_notice: "Privacy notice",
    },
    update: {
      available: "There is a new version of the app",
      later: "Later",
      now: "Update",
    },
    about: {
      app: "Amai Mochi",
      updated: "Updated on %{date}",
      made_by: "Made by Leo Fuentes for Amai Mochi",
      help: "Help (WhatsApp with Leo)",
      help_message: "Hi Leo, I have a question about the Amai Mochi app: ",
    },
    expenses: {
      title: "Expenses",
      amount: "Amount",
      category: "Category",
      categories: {
        ingredientes: "Ingredients",
        empaque: "Packaging",
        transporte: "Fuel and transport",
        otro: "Other",
      },
      date: "Date",
      description: "Note (optional)",
      description_placeholder: "What was it?",
      save: "Save expense",
      saved: "Expense saved",
      save_error:
        "The expense could not be saved. Check your connection and try again.",
      recent: "Recent expenses",
      this_week: "This week: %{total}",
      empty: "No expenses in the last weeks.",
      delete: "Delete %{name}",
      deleted: "Expense deleted",
    },
    prices: {
      title: "Prices",
      intro:
        "Price per piece of each flavor. New orders use it; saved orders keep the price they had.",
      same_for_all: "Same price for all",
      apply_to_all: "Apply to all",
      save: "Save prices",
      saved: "Prices saved",
      save_error:
        "Some prices could not be saved. Check your connection and try again.",
      invalid: "Write each price as a number, like 25 or 25.50.",
    },
    clients: {
      add: "New client",
      create_title: "New client",
      edit_title: "Edit client",
      empty: "No clients yet. Add the first one.",
      inactive: "Inactive",
      phone_hint: "10 digits. Used to message them on WhatsApp.",
      invalid_phone: "Write 10 digits, or 12 with 52 in front.",
      zona_placeholder: "Guadalajara, Colima, Tepetitlán…",
      history: {
        title: "Client history",
        bought: "Bought",
        orders: "Orders",
        owes: "Owes",
        latest: "Latest orders",
        none: "No orders yet.",
        data: "Details",
      },
    },
  },
  resources: {
    sabor: {
      name: "Flavor |||| Flavors",
      fields: {
        nombre: "Name",
        color: "Color",
        activo: "Active",
      },
    },
    cliente: {
      name: "Client |||| Clients",
      fields: {
        nombre: "Name",
        tipo: "Type",
        zona: "Area",
        telefono: "Phone",
        notas: "Notes",
        activo: "Active",
      },
      tipos: {
        distribuidor: "Distributor",
        publico: "Public",
        evento: "Event",
      },
    },
  },
};

export const spanishAmaiMessages = {
  amai: {
    navigation: {
      label: "Navegación de Amai Mochi",
      new_order: "Nuevo pedido",
      orders: "Pedidos",
      receivables: "Por cobrar",
      summary: "Resumen",
      settings: "Ajustes",
    },
    catalog: {
      title: "Catálogo",
    },
    flavors: {
      add: "Nuevo sabor",
      create_title: "Nuevo sabor",
      edit_title: "Editar sabor",
      empty: "Todavía no hay sabores. Agrega el primero.",
      inactive: "Inactivo",
      active_toggle: "Activo: %{name}",
      duplicate: "Ya existe un sabor con ese nombre.",
      color_hint: "Se muestra junto al sabor en pedidos y gráficas.",
      invalid_color: "Elige un color, por ejemplo #EE8FA8.",
    },
    orders: {
      delivery: "Entrega",
      delivery_options: {
        manana: "Mañana",
        pasado: "Pasado mañana",
        otra: "Otra fecha",
      },
      delivery_date: "Fecha de entrega",
      pieces: "%{smart_count} pieza |||| %{smart_count} piezas",
      pieces_of: "Piezas de %{name}",
      remove_one: "Quitar una pieza de %{name}",
      add_five: "Agregar 5 piezas de %{name}",
      no_flavors: "No hay sabores activos. Agrégalos en Ajustes → Sabores.",
      minimum_ok: "Cumple el mínimo de %{minimum} piezas",
      minimum_missing: "Faltan %{missing} piezas para el mínimo de %{minimum}",
      payment: "Pago al recibir",
      payment_methods: {
        efectivo: "Efectivo",
        transferencia: "Transferencia",
      },
      location_and_notes: "Notas y ubicación",
      location: "Ubicación",
      location_placeholder: "Dirección o link de Maps",
      notes: "Notas",
      save: "Guardar pedido",
      saving: "Guardando…",
      saved: "Pedido guardado. Queda pendiente de entrega y cobro.",
      save_error:
        "No se pudo guardar el pedido. Revisa tu conexión e inténtalo otra vez.",
      missing_client: "Elige un cliente",
      missing_pieces: "Agrega al menos un sabor",
      missing_date: "Elige la fecha de entrega",
      view_orders: "Ver pedidos",
      repeat: {
        last: "Último pedido (%{date}):",
        button: "Repetir el último pedido",
      },
      confirm: {
        button: "Confirmar por WhatsApp",
        greeting: "Hola %{name}, te confirmo tu pedido de Amai Mochi:",
        total:
          "Total: %{smart_count} pieza · %{total} |||| Total: %{smart_count} piezas · %{total}",
        total_no_price:
          "Total: %{smart_count} pieza |||| Total: %{smart_count} piezas",
        delivery: "Entrega: %{date}",
        payment: "Pago al recibir: %{method}",
        thanks: "¡Gracias!",
      },
      filter: "Mostrar",
      filters: {
        pendiente: "Pendientes",
        entregado: "Entregados",
        cancelado: "Cancelados",
        todos: "Todos",
      },
      states: {
        pendiente: "Pendiente",
        entregado: "Entregado",
        cancelado: "Cancelado",
      },
      days: {
        yesterday: "Ayer",
        today: "Hoy",
        tomorrow: "Mañana",
      },
      empty: "No hay pedidos aquí.",
      no_price: "Sin precio",
      actions: {
        deliver: "Entregado",
        edit: "Editar",
        cancel: "Cancelar",
        back_to_pending: "Regresar a pendiente",
      },
      notice: {
        delivered: "Pedido marcado como entregado",
        cancelled: "Pedido cancelado",
        pending: "El pedido volvió a pendiente",
      },
      edit_title: "Editar pedido",
      save_changes: "Guardar cambios",
      updated: "Pedido actualizado",
      only_pending: "Solo se pueden editar pedidos pendientes.",
      open_maps: "Abrir %{address} en Maps",
    },
    production: {
      title: "Producción",
      for: "Preparar para",
      days: {
        hoy: "Hoy",
        manana: "Mañana",
        pasado: "Pasado mañana",
      },
      total: "%{smart_count} pieza |||| %{smart_count} piezas",
      orders: "para %{smart_count} pedido |||| para %{smart_count} pedidos",
      per_flavor: "Piezas por sabor",
      empty: "No hay pedidos pendientes para ese día.",
    },
    deliveries: {
      title: "Entregas de hoy",
      progress: "%{done} de %{total} entregados · faltan %{pieces} piezas",
      overdue: "Atrasados",
      empty: "No hay entregas para hoy.",
      all_orders: "Ver todos los pedidos",
    },
    summary: {
      title: "Tu semana",
      previous_week: "Semana anterior",
      next_week: "Semana siguiente",
      sold: "Vendiste",
      vs_last_week: "%{change} vs. semana pasada",
      orders: "Pedidos",
      average_pieces:
        "%{smart_count} pz en promedio |||| %{smart_count} pzs en promedio",
      collected: "Cobrado",
      to_collect: "Por cobrar",
      expenses: "Gastos",
      profit: "Ganancia",
      profit_hint: "Ventas menos gastos",
      no_sales: "No hubo entregas esta semana.",
      pieces_per_day: "Piezas por día",
      day_pieces:
        "%{day}: %{smart_count} pieza |||| %{day}: %{smart_count} piezas",
      top_flavors: "Sabores más vendidos",
      flavor_pieces:
        "%{name}: %{smart_count} pieza |||| %{name}: %{smart_count} piezas",
      by_client: "Por cliente",
    },
    receivables: {
      summary: "Resumen de lo que te deben",
      owed_total: "Te deben en total",
      all_paid: "Todo cobrado",
      counts:
        "%{smart_count} pedido · %{overdue} con más de %{days} días |||| %{smart_count} pedidos · %{overdue} con más de %{days} días",
      filters: {
        todos: "Todos",
        vencidos: "Más de %{days} días",
      },
      none_overdue: "Ningún cliente tiene pedidos con más de %{days} días.",
      order_line: "%{date} · %{pieces} pzs",
      delivered_today: "hoy",
      days_ago: "hace %{smart_count} día |||| hace %{smart_count} días",
      remind: "Recordar",
      reminded: "Recordado",
      no_phone: "Sin teléfono para recordar",
      register_payment: "Registrar pago",
      how_paid: "¿Cómo pagó?",
      paid: "Pago de %{name} registrado",
      pay_error:
        "No se pudo registrar el pago. Revisa tu conexión e inténtalo otra vez.",
      undone: "Pago deshecho",
      collected_today: "Cobrado hoy",
      no_price_hint:
        "Hay un pedido sin precio: captura los precios para poder cobrarlo.",
      reminder:
        "Hola %{name}, te escribo de Amai Mochi. Te recuerdo el pago pendiente de %{total} por %{smart_count} pedido. ¡Muchas gracias! |||| Hola %{name}, te escribo de Amai Mochi. Te recuerdo el pago pendiente de %{total} por %{smart_count} pedidos. ¡Muchas gracias!",
    },
    export: {
      title: "Exportar a Excel",
      intro:
        "Baja las ventas, los cobros o los gastos de un mes en un archivo que se abre en Excel, para mandárselo a tu contador.",
      month: "Mes",
      reports: {
        ventas: "Ventas (pedidos entregados)",
        cobros: "Cobros",
        gastos: "Gastos",
      },
      empty: "No hay nada que exportar en ese mes.",
      columns: {
        date: "Fecha",
        order: "Pedido",
        client: "Cliente",
        flavors: "Sabores",
        pieces: "Piezas",
        payment: "Forma de pago",
        total: "Total",
        reference: "Referencia",
        amount: "Monto",
        category: "Categoría",
        note: "Nota",
      },
    },
    money: {
      title: "Dinero",
    },
    legal: {
      privacy_notice: "Aviso de privacidad",
    },
    update: {
      available: "Hay una versión nueva de la app",
      later: "Después",
      now: "Actualizar",
    },
    about: {
      app: "Amai Mochi",
      updated: "Actualizada el %{date}",
      made_by: "Hecha por Leo Fuentes para Amai Mochi",
      help: "Ayuda (WhatsApp con Leo)",
      help_message: "Hola Leo, tengo una duda con la app de Amai Mochi: ",
    },
    expenses: {
      title: "Gastos",
      amount: "Monto",
      category: "Categoría",
      categories: {
        ingredientes: "Ingredientes",
        empaque: "Empaque",
        transporte: "Gasolina y transporte",
        otro: "Otro",
      },
      date: "Fecha",
      description: "Nota (opcional)",
      description_placeholder: "¿En qué se gastó?",
      save: "Guardar gasto",
      saved: "Gasto guardado",
      save_error:
        "No se pudo guardar el gasto. Revisa tu conexión e inténtalo otra vez.",
      recent: "Gastos recientes",
      this_week: "Esta semana: %{total}",
      empty: "No hay gastos en las últimas semanas.",
      delete: "Borrar %{name}",
      deleted: "Gasto borrado",
    },
    prices: {
      title: "Precios",
      intro:
        "Precio por pieza de cada sabor. Lo usan los pedidos nuevos; los pedidos ya guardados conservan su precio.",
      same_for_all: "Mismo precio para todos",
      apply_to_all: "Aplicar a todos",
      save: "Guardar precios",
      saved: "Precios guardados",
      save_error:
        "Algunos precios no se guardaron. Revisa tu conexión e inténtalo otra vez.",
      invalid: "Escribe cada precio como número, por ejemplo 25 o 25.50.",
    },
    clients: {
      add: "Nuevo cliente",
      create_title: "Nuevo cliente",
      edit_title: "Editar cliente",
      empty: "Todavía no hay clientes. Agrega el primero.",
      inactive: "Inactivo",
      phone_hint: "10 dígitos. Sirve para escribirle por WhatsApp.",
      invalid_phone: "Escribe 10 dígitos, o 12 con 52 al inicio.",
      zona_placeholder: "Guadalajara, Colima, Tepetitlán…",
      history: {
        title: "Historial del cliente",
        bought: "Ha comprado",
        orders: "Pedidos",
        owes: "Debe",
        latest: "Últimos pedidos",
        none: "Todavía no tiene pedidos.",
        data: "Datos",
      },
    },
  },
  resources: {
    sabor: {
      name: "Sabor |||| Sabores",
      fields: {
        nombre: "Nombre",
        color: "Color",
        activo: "Activo",
      },
    },
    cliente: {
      name: "Cliente |||| Clientes",
      fields: {
        nombre: "Nombre",
        tipo: "Tipo",
        zona: "Zona",
        telefono: "Teléfono",
        notas: "Notas",
        activo: "Activo",
      },
      tipos: {
        distribuidor: "Distribuidor",
        publico: "Público",
        evento: "Evento",
      },
    },
  },
};
